import DataFormatter from "../../DataFormatter";

export const VALID = { isValid: true, message: null };

export const isBlank = (value) => !value || !/\S/.test(value);

const MAX_QUANTITY = 6000;

// M/D/YY, M/D/YYYY, MM/DD/YY, MM/DD/YYYY with slashes (2 or 4 digit year)
const SLASH_MDY = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;
// M-D-YYYY, MM-DD-YYYY with dashes (4 digit year only)
const DASH_MDY = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
// YYYY/M/D, YYYY-M-D (year-first, either separator)
const YMD = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/;

const isOnOrBeforeToday = (val) => {
  const date = new Date(DataFormatter.formatDate(val));
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date <= today;
};

export const isValidDate = (val) => {
  if (!val) return false;
  const trimmed = val.trim();

  let year, month, day;

  let match = trimmed.match(YMD);
  if (match) {
    year = parseInt(match[1], 10);
    month = parseInt(match[2], 10);
    day = parseInt(match[3], 10);
  } else {
    match = trimmed.match(SLASH_MDY) || trimmed.match(DASH_MDY);
    if (!match) return false;
    month = parseInt(match[1], 10);
    day = parseInt(match[2], 10);
    year = parseInt(match[3], 10);
    if (year < 100) year += 2000;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

export const isValidDateOfService = (val, datePrescribed) => {
  const today = Date.parse(Date());
  const dos = Date.parse(DataFormatter.formatDate(val));

  if (dos > today) return false;

  if (datePrescribed && isValidDate(datePrescribed)) {
    const dp = Date.parse(DataFormatter.formatDate(datePrescribed));
    if (dos < dp) return false;
  }

  return true;
};

export const isValidDatePrescribed = (val, dateOfService) => {
  const today = Date.parse(Date());
  const dp = Date.parse(DataFormatter.formatDate(val));

  if (dp > today) return false;

  if (dateOfService && isValidDate(dateOfService)) {
    const dos = Date.parse(DataFormatter.formatDate(dateOfService));
    if (dos < dp) return false;
  }

  return true;
};

export const isValidRXNumber = (val) => {
  if (!val) return false;

  const reg = /^\d{5,12}(-\d{1,2})?$/;
  return reg.test(val);
};

export const isValidNDC = (val) => {
  if (!val) return false;

  const reg = /^(?:0\d{10}|[1-9]\d{6,10}|\d{5}-\d{4}-\d{2}|\d{6}-\d{3}-\d{2}|\d{6}-\d{4}-\d{1})$/;
  return reg.test(val);
};

export const isValidQuantity = (val) => {
  if (!val) return false;
  if (!/^-?\d*(\.\d+)?$/.test(val)) return false;

  return !isNaN(parseFloat(val));
};

export const isQuestionableQuantity = (val) => {
  return parseFloat(val) > MAX_QUANTITY;
};

export const isValidWholesalerInvoiceNumber = (val) => {
  if (!val || !val.trim()) return true;
  const regex = /^[A-Za-z0-9\-,.#]+$/;
  return regex.test(val);
};

export const isValidNPI = (val) => {
  if (!val) return false;
  // 10 digits, no leading zero
  const reg = /^[1-9]{1}\d{9}$/;
  return reg.test(val) && luhnCheck(val);
};

export const isValidNCPDP = (val) => {
  if (!val) return false;
  // 7 digits, at most 1 leading zero
  const reg = /^(?:[1-9]\d{6}|0[1-9]\d{5})$/;
  return reg.test(val) && nabpCheck(val);
};

export const isValidDEA = (val) => {
  if (!val) return false;
  // first character must be one of the following: (A|B|C|D|E|F|G|H|J|K|L|M|P|R|S|T|U|X)
  const reg = /^[A-HJK-MPR-UX][A-Za-z0-9][\d]{7}$/;
  return reg.test(val) && deaCheck(val);
};

export const isValidId340b = (val) => {
  if (!val) return false;
  const reg =
    /^(?=.{4,13}$)(?:BL|CAH|CAN|CH|DSH|FP|FQ|HM|HV|NH|PED|RRC|RW4|RWI|SCH|SPNS|STD|TB|UI|URB)[a-zA-Z0-9-]*[a-zA-Z0-9]$/;
  return reg.test(val);
};

export const isValidFillNumber = (val) => {
  if (!val) return true;
  return /^\d{1,2}$/.test(val.trim());
};

export const isValidPrescriberIDQualifier = (val) => {
  if (!val) return true;
  return /^(01|1|12)$/.test(val);
};

export const isValidServiceProviderIDQualifier = (val) => {
  if (!val) return true;
  return /^(01|1|05|5|07|7|10|12)$/.test(val);
};

export const isValidPayerBin = (val) => {
  if (!val) return true;
  return /^[a-zA-Z0-9]{1,10}$/.test(val);
};

export const isValidPCN = (val) => {
  if (!val) return true;
  return /^[a-zA-Z0-9\s]{1,20}$/.test(val);
};

export const isValidAccount340b = (val) => {
  if (!val) return true;
  return /^[a-zA-Z0-9]{1,20}$/.test(val);
};

export const isValidProductSerializationNumber = (val) => {
  if (!val) return true;
  return /^[a-zA-Z0-9]{1,20}$/.test(val);
};

export const isValidHealthPlanId = (val) => {
  if (!val) return false;
  return /^[a-zA-Z0-9-]{1,17}$/.test(val);
};

export const isValidHealthPlanName = (val) => {
  if (!val) return false;
  return /^[a-zA-Z0-9 /-]{1,100}$/.test(val);
};

export const isValidClaimNumber = (val) => {
  if (!val) return false;
  const cleaned = val.replace(/-/g, "").replace(/^0+/, "");
  return /^[a-zA-Z0-9]{1,40}$/.test(cleaned);
};

export const isValidClaimLineNumber = (val) => {
  if (!val) return false;
  if (!/^\d+$/.test(val)) return false;

  const maxVal = 2_147_483_647; // 2^31 - 1
  return parseInt(val, 10) <= maxVal;
};

export const isValidHCPCS = (val) => {
  if (!val || val.trim() === "") return true;
  return /^[a-zA-Z0-9]{5}$/.test(val);
};

export const isValidHCPCSMod = (val) => {
  if (!val || val.trim() === "") return true;
  return /^[a-zA-Z0-9]{2}$/.test(val);
};

export const isValidUnitOfMeasure = (val) => {
  if (!val) return true;
  // ea, gm, or ml (case-insensitive, no internal whitespace)
  const reg = /^(ea|gm|ml)$/i;
  return reg.test(val.trim());
};

export const isValidIdentifier = (val) => {
  return isValidNPI(val) || isValidNCPDP(val) || isValidDEA(val);
};

export const isDateInRange = (date, range) => {
  if (isNaN(Date.parse(DataFormatter.formatDate(date)))) return;

  const timeInDay = 1000 * 60 * 60 * 24;
  const dateToday = DataFormatter.formatDate(
    new Date().toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })
  );
  const daysBetween =
    (Date.parse(dateToday) - Date.parse(DataFormatter.formatDate(date))) / timeInDay;

  if (daysBetween >= 0 && daysBetween <= range) {
    return "true";
  } else {
    return "false";
  }
};

const luhnCheck = (val) => {
  let nCheck = 24,
    bEven = false;
  val = val.replace(/\D/g, "");

  for (let n = val.length - 1; n >= 0; n--) {
    let cDigit = val.charAt(n),
      nDigit = parseInt(cDigit, 10);
    if (bEven && (nDigit *= 2) > 9) nDigit -= 9;
    nCheck += nDigit;
    bEven = !bEven;
  }

  return nCheck % 10 === 0;
};

const nabpCheck = (val) => {
  const a = parseInt(val.charAt(0)) + parseInt(val.charAt(2)) + parseInt(val.charAt(4));
  const b = 2 * (parseInt(val.charAt(1)) + parseInt(val.charAt(3)) + parseInt(val.charAt(5)));
  return (a + b) % 10 === parseInt(val.charAt(6));
};

const deaCheck = (val) => {
  const array = val.split("");
  const uid = array.slice(2, 8);
  const checksum = array[array.length - 1];

  const value = (
    parseInt(uid[0]) +
    parseInt(uid[2]) +
    parseInt(uid[4]) +
    2 * (parseInt(uid[1]) + parseInt(uid[3]) + parseInt(uid[5]))
  )
    .toString()
    .split("");

  return value[value.length - 1] === checksum;
};

export const isValidNonFutureDate = (val) => {
  if (!isValidDate(val)) return false;

  return isOnOrBeforeToday(val);
};

export const isConformingDateOfEncounter = (dateOfEncounter, dateOfService) => {
  if (!dateOfEncounter) return true;
  if (!dateOfService) return false;
  const formattedEncounter = DataFormatter.formatDate(dateOfEncounter);
  const formattedService = DataFormatter.formatDate(dateOfService);
  if (!formattedEncounter) return true;
  if (!formattedService) return false;
  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = Math.abs(Date.parse(formattedEncounter) - Date.parse(formattedService));
  return Math.floor(diff / msPerDay) <= 365;
};

export const isValidDiagnosisCode = (val) => {
  if (!val) return true;
  return /^[A-Za-z][A-Za-z0-9]{2,6}$/.test(val.replaceAll(".", ""));
};

export const isValidPlaceOfService = (val) => {
  if (!val) return true;
  return /^\d{1,2}$/.test(val);
};

export const hcpcsOrUnitOfMeasureCrossFieldErrors = (data, translator) => {
  const errors = [];
  const hasUom = Boolean(data["unit_of_measure"]?.trim());
  const hasHcpcs = Boolean(data["hcpcs_code"]?.trim());

  if ((!hasUom && !hasHcpcs) || (hasUom && hasHcpcs)) {
    const message = translator.getError("hcpcs_xor_uom_required");
    errors.push({ field: "unit_of_measure", message });
    errors.push({ field: "hcpcs_code", message });
  }

  return errors;
};
